export type BusinessUnitOption = {
  id: string;
  name: string;
};

export const BUSINESS_UNIT_TAG_GAP_PX = 4;
export const BUSINESS_UNIT_OVERFLOW_CHIP_WIDTH_PX = 40;
export const BUSINESS_UNIT_INPUT_RESERVED_WIDTH_PX = 80;
export const BUSINESS_UNIT_TAG_MAX_WIDTH_PX = 200;

const sumTagWidths = ({
  tagWidths,
  count,
}: {
  tagWidths: readonly number[];
  count: number;
}): number =>
  tagWidths
    .slice(0, count)
    .reduce(
      (total, width, index) =>
        total + width + (index > 0 ? BUSINESS_UNIT_TAG_GAP_PX : 0),
      0,
    );

export const calculateVisibleTagCount = ({
  controlWidth,
  tagWidths,
  overflowChipWidth,
  inputReservedWidthPx,
}: {
  controlWidth: number;
  tagWidths: readonly number[];
  overflowChipWidth: number;
  inputReservedWidthPx: number;
}): number => {
  const tagsAreaWidth = Math.max(0, controlWidth - inputReservedWidthPx);

  if (tagWidths.length === 0 || tagsAreaWidth <= 0) {
    return 0;
  }

  const fittingCount = tagWidths.findIndex(
    (_, index) => sumTagWidths({ tagWidths, count: index + 1 }) > tagsAreaWidth,
  );
  const unconstrainedCount =
    fittingCount === -1 ? tagWidths.length : fittingCount;

  if (unconstrainedCount >= tagWidths.length) {
    return unconstrainedCount;
  }

  const fitsWithOverflow = (count: number) =>
    count > 0 &&
    sumTagWidths({ tagWidths, count }) +
      BUSINESS_UNIT_TAG_GAP_PX +
      overflowChipWidth <=
      tagsAreaWidth;
  const countWithOverflow =
    Array.from(
      { length: unconstrainedCount },
      (_, index) => unconstrainedCount - index,
    ).find(fitsWithOverflow) ?? 0;

  return countWithOverflow === 0 && unconstrainedCount >= 1
    ? 1
    : countWithOverflow;
};

export const filterBusinessUnitsByQuery = <TUnit extends BusinessUnitOption>({
  businessUnits,
  query,
}: {
  businessUnits: readonly TUnit[];
  query: string;
}): readonly TUnit[] => {
  if (query === '') {
    return businessUnits;
  }

  const normalizedQuery = query.toLowerCase();

  return businessUnits.filter(({ name }) =>
    name.toLowerCase().includes(normalizedQuery),
  );
};

export const isExistingBusinessUnitName = ({
  query,
  businessUnits,
}: {
  query: string;
  businessUnits: readonly BusinessUnitOption[];
}): boolean => {
  const key = query.trim().toLowerCase();

  return (
    key.length > 0 &&
    businessUnits.some(
      (businessUnit) => businessUnit.name.toLowerCase() === key,
    )
  );
};

export const toggleBusinessUnit = ({
  value,
  businessUnit,
}: {
  value: readonly BusinessUnitOption[];
  businessUnit: BusinessUnitOption;
}): BusinessUnitOption[] =>
  value.some(({ id }) => id === businessUnit.id)
    ? value.filter(({ id }) => id !== businessUnit.id)
    : [...value, { id: businessUnit.id, name: businessUnit.name }];

export const resolveBusinessUnitTagChange = ({
  nextTagNames,
  value,
  allBusinessUnits,
}: {
  nextTagNames: readonly string[];
  value: readonly BusinessUnitOption[];
  allBusinessUnits: readonly BusinessUnitOption[];
}): { selected: BusinessUnitOption[]; missingNames: string[] } => {
  const businessUnitsByName = new Map(
    [...allBusinessUnits, ...value].map((businessUnit) => [
      businessUnit.name.toLowerCase(),
      businessUnit,
    ]),
  );
  const normalizedTagNames = nextTagNames
    .map((tagName) => tagName.trim())
    .filter((tagName) => tagName.length > 0);
  const nextNameKeys = new Set(
    normalizedTagNames.map((tagName) => tagName.toLowerCase()),
  );
  const keptFromValue = value.filter(
    (businessUnit, index) =>
      nextNameKeys.has(businessUnit.name.toLowerCase()) &&
      value.findIndex(
        ({ name }) => name.toLowerCase() === businessUnit.name.toLowerCase(),
      ) === index,
  );
  const keptKeys = new Set(keptFromValue.map(({ name }) => name.toLowerCase()));
  const remainingTagNames = normalizedTagNames.filter(
    (tagName, index) =>
      !keptKeys.has(tagName.toLowerCase()) &&
      normalizedTagNames.findIndex(
        (other) => other.toLowerCase() === tagName.toLowerCase(),
      ) === index,
  );
  const addedExisting = remainingTagNames.flatMap((tagName) => {
    const existing = businessUnitsByName.get(tagName.toLowerCase());

    return existing ? [existing] : [];
  });
  const missingNames = remainingTagNames.filter(
    (tagName) => !businessUnitsByName.has(tagName.toLowerCase()),
  );

  return {
    selected: [...keptFromValue, ...addedExisting],
    missingNames,
  };
};
